import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Building2, BookOpen } from 'lucide-react';
import ReviewsWidgetSettings from './ReviewsWidgetSettings';
import WidgetSettings from '../settings/WidgetSettings';
import WidgetDocs from './WidgetDocs';

type WidgetTab = 'public' | 'backoffice' | 'docs';

/**
 * Top-level hub for the vendor's embeddable widgets: the public reviews widget
 * (showcases published reviews on the vendor's own site) and the backoffice
 * widget (lets the vendor's users self-sign up as promoters).
 */
const WidgetManagement: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<WidgetTab>('public');

  const tabs: { id: WidgetTab; label: string; icon: React.ElementType }[] = [
    { id: 'public', label: t('reviewsWidget.tabs.public', 'Website / Public Widget'), icon: Globe },
    { id: 'backoffice', label: t('reviewsWidget.tabs.backoffice', 'Backoffice Widget'), icon: Building2 },
    { id: 'docs', label: t('reviewsWidget.tabs.docs', 'Documentation'), icon: BookOpen },
  ];

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('reviewsWidget.pageTitle', 'Widget Management')}</h1>
        <p className="text-sm text-gray-600">
          {t('reviewsWidget.pageSubtitle', 'Manage widgets that showcase your customer advocacy.')}
        </p>
      </div>

      {/* Tabs */}
      <div className="inline-flex items-center gap-1 bg-gray-100 rounded-xl p-1">
        {tabs.map((tb) => {
          const active = tab === tb.id;
          return (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                active ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tb.icon className="w-4 h-4" />
              {tb.label}
            </button>
          );
        })}
      </div>

      {tab === 'public' && <ReviewsWidgetSettings />}
      {tab === 'backoffice' && <WidgetSettings />}
      {tab === 'docs' && <WidgetDocs />}
    </div>
  );
};

export default WidgetManagement;
